import { useEffect, useState, useRef, useCallback } from "react";
import { QRCodeSVG } from "qrcode.react";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import ConnectionHealth from "@/components/ConnectionHealth";
import { TranscriptionCredits } from "@/components/app/TranscriptionCredits";
import { useAuth } from "@/contexts/AuthContext";
import {
  WhatsappLogo, Phone, Plug, PlugsConnected, ArrowClockwise,
  Warning, CheckCircle, Prohibit, LinkBreak, QrCode, Star, Plus, Trash,
  Robot, SpeakerHigh, PhoneSlash,
} from "@phosphor-icons/react";
import { Switch } from "@/components/ui/switch";

const STATE_META = {
  open: { label: "Conectado", color: "#0F4C3A", icon: CheckCircle },
  connecting: { label: "Aguardando pareamento", color: "#D97706", icon: QrCode },
  close: { label: "Desconectado", color: "#DC2626", icon: LinkBreak },
  not_created: { label: "Instância não criada", color: "#5C5C59", icon: Plug },
  not_configured: { label: "Evolution API não configurada", color: "#DC2626", icon: Warning },
  unknown: { label: "Estado desconhecido", color: "#5C5C59", icon: Warning },
  not_found: { label: "Instância não encontrada", color: "#5C5C59", icon: Warning },
};

const QR_TTL_SECONDS = 40;

export default function CompanyConnections() {
  const { user } = useAuth();
  const [data, setData] = useState({ items: [], plan_max_connections: 0, used: 0, remaining: 0 });
  const [loading, setLoading] = useState(true);
  const [selectedId, setSelectedId] = useState(null);
  const [showNew, setShowNew] = useState(false);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/whatsapp/connections");
      setData(data);
      if (!selectedId && data.items.length) {
        setSelectedId(data.items[0].id);
      }
      return data;
    } catch (err) {
      toast.error(formatApiError(err));
      return null;
    }
  }, [selectedId]);

  useEffect(() => {
    (async () => {
      await load();
      setLoading(false);
    })();
  }, [load]);

  // Poll every 5s so states stay fresh
  useEffect(() => {
    const t = setInterval(load, 5000);
    return () => clearInterval(t);
  }, [load]);

  const selected = data.items.find((c) => c.id === selectedId) || data.items[0] || null;
  const atLimit = data.remaining <= 0;

  if (loading) {
    return <div className="p-8 text-sm text-[#5C5C59]">Carregando…</div>;
  }

  return (
    <div className="p-8 max-w-6xl">
      <div className="flex items-start justify-between flex-wrap gap-4 mb-8">
        <div>
          <p className="text-xs font-semibold tracking-[0.3em] uppercase text-[#0F4C3A]">Integração</p>
          <h1 className="text-4xl font-black tracking-tighter text-[#1A1A1A] mt-1">Conexões WhatsApp</h1>
          <p className="text-sm text-[#5C5C59] mt-2">
            Vincule múltiplos números da sua empresa. Cada número aparece separadamente no Kanban.
          </p>
        </div>
        <div className="flex items-center gap-4">
          <div className="text-right">
            <div className="text-[10px] uppercase tracking-[0.25em] font-semibold text-[#5C5C59]">Plano</div>
            <div className="font-mono text-2xl font-black text-[#0F4C3A]" data-testid="connections-quota">
              {data.used}<span className="text-[#5C5C59]">/{data.plan_max_connections}</span>
            </div>
            <div className="text-[10px] text-[#5C5C59]">conexões usadas</div>
          </div>
          <Button
            onClick={() => setShowNew(true)}
            disabled={atLimit}
            data-testid="new-connection-btn"
            className="bg-[#0F4C3A] hover:bg-[#0A3629] text-white rounded-none shadow-brutal disabled:opacity-40 disabled:cursor-not-allowed"
          >
            <Plus size={18} weight="bold" className="mr-2" />
            Nova conexão
          </Button>
        </div>
      </div>

      <TranscriptionCredits canBuy={["company_owner", "master"].includes(user?.role)} />

      {atLimit && (
        <div className="mb-6 p-4 bg-[#FEF3C7] border-l-[3px] border-[#D97706] text-sm" data-testid="plan-limit-banner">
          <span className="font-semibold text-[#92400E]">Limite do plano atingido.</span>
          <span className="text-[#78350F] ml-1">
            Peça ao master para aumentar <span className="font-mono">max_connections</span> no seu plano.
          </span>
        </div>
      )}

      {data.items.length === 0 ? (
        <EmptyState onCreate={() => setShowNew(true)} disabled={atLimit} />
      ) : (
        <div className="grid md:grid-cols-[320px_1fr] gap-6">
          <ConnectionList
            items={data.items}
            selectedId={selected?.id}
            onSelect={setSelectedId}
          />
          {selected && (
            <ConnectionDetail
              key={selected.id}
              connection={selected}
              onChange={load}
            />
          )}
        </div>
      )}

      <NewConnectionDialog
        open={showNew}
        onOpenChange={setShowNew}
        onCreated={async (created) => {
          await load();
          setSelectedId(created.id);
        }}
      />

      {/* Diagnostic is ALWAYS shown so the user can spot the Meta-AI /
          webhook-not-arriving conflict even before pairing succeeds. */}
      <DiagnosticPanel />
    </div>
  );
}

// -------------------- List --------------------
function ConnectionList({ items, selectedId, onSelect }) {
  return (
    <div className="border border-[#E5E5E0] bg-white shadow-brutal" data-testid="connections-list">
      <div className="px-4 py-2 border-b border-[#E5E5E0] text-[10px] uppercase tracking-[0.25em] font-semibold text-[#5C5C59]">
        Números
      </div>
      {items.map((c) => {
        const meta = STATE_META[c.state] || STATE_META.unknown;
        const Icon = meta.icon;
        const active = c.id === selectedId;
        return (
          <button
            key={c.id}
            onClick={() => onSelect(c.id)}
            data-testid={`connection-row-${c.id}`}
            className={`w-full text-left px-4 py-3 border-b border-[#E5E5E0] last:border-b-0 flex items-center gap-3 transition-colors duration-150 ${
              active ? "bg-[#0F4C3A] text-white" : "bg-white hover:bg-[#F7F7F5]"
            }`}
          >
            <Icon size={20} weight="duotone" style={{ color: active ? meta.color === "#0F4C3A" ? "#22A882" : meta.color : meta.color }} />
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-1.5">
                {c.is_primary && <Star size={12} weight="fill" className={active ? "text-[#FFD700]" : "text-[#D97706]"} />}
                <span className="font-semibold truncate">{c.label || (c.owner ? c.owner.replace("@s.whatsapp.net", "") : "Sem rótulo")}</span>
              </div>
              <div className={`text-[10px] mt-0.5 truncate ${active ? "text-[#c8d5cf]" : "text-[#5C5C59]"}`}>
                {meta.label}
              </div>
            </div>
          </button>
        );
      })}
    </div>
  );
}

// -------------------- Detail --------------------
function ConnectionDetail({ connection, onChange }) {
  const [qrCode, setQrCode] = useState(null);
  const [qrAgeSec, setQrAgeSec] = useState(0);
  const qrFetchedAtRef = useRef(null);
  const qrAgeTickRef = useRef(null);
  const [actionLoading, setActionLoading] = useState(false);

  const loadQr = useCallback(async () => {
    try {
      const { data } = await api.get(`/whatsapp/connections/${connection.id}/qrcode`);
      if (data?.code) {
        setQrCode(data.code);
        qrFetchedAtRef.current = Date.now();
        setQrAgeSec(0);
      }
    } catch (err) {
      // ignore
    }
  }, [connection.id]);

  useEffect(() => {
    // fetch a QR when this connection is not yet open
    if (connection.state === "connecting" || connection.state === "close") {
      loadQr();
    } else {
      setQrCode(null);
    }
  }, [connection.state, connection.id, loadQr]);

  useEffect(() => {
    clearInterval(qrAgeTickRef.current);
    if ((connection.state === "connecting" || connection.state === "close") && qrCode) {
      qrAgeTickRef.current = setInterval(() => {
        const age = qrFetchedAtRef.current
          ? Math.floor((Date.now() - qrFetchedAtRef.current) / 1000)
          : 0;
        setQrAgeSec(age);
        if (age >= QR_TTL_SECONDS) loadQr();
      }, 1000);
    }
    return () => clearInterval(qrAgeTickRef.current);
  }, [connection.state, qrCode, loadQr]);

  const refresh = async () => {
    setActionLoading(true);
    await onChange?.();
    await loadQr();
    setActionLoading(false);
    toast.info("QR atualizado");
  };

  const disconnect = async () => {
    if (!window.confirm(`Remover a conexão "${connection.label || connection.instance_name}"?`)) return;
    setActionLoading(true);
    try {
      await api.delete(`/whatsapp/connections/${connection.id}`);
      toast.success("Conexão removida");
      await onChange?.();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setActionLoading(false);
    }
  };

  const state = connection.state || "not_created";
  const meta = STATE_META[state] || STATE_META.unknown;
  const Icon = meta.icon;
  const secondsLeft = Math.max(0, QR_TTL_SECONDS - qrAgeSec);

  return (
    <div>
      <div className="bg-white border border-[#E5E5E0] shadow-brutal p-6" data-testid="connection-detail">
        <div className="flex items-start justify-between flex-wrap gap-4">
          <div className="flex items-start gap-4">
            <div className="h-14 w-14 flex items-center justify-center" style={{ background: meta.color + "1A", color: meta.color }}>
              <Icon size={30} weight="duotone" />
            </div>
            <div>
              <p className="text-[10px] uppercase tracking-[0.3em] font-semibold text-[#5C5C59] flex items-center gap-1.5">
                {connection.is_primary && <Star size={11} weight="fill" className="text-[#D97706]" />}
                {connection.is_primary ? "Conexão primária" : "Conexão"}
              </p>
              <h2 className="text-2xl font-bold tracking-tight" style={{ color: meta.color }} data-testid="connection-state-label">
                {connection.label || meta.label}
              </h2>
              <div className="text-sm text-[#5C5C59] mt-0.5">Status: {meta.label}</div>
              {connection.owner && (
                <div className="mt-2 flex items-center gap-2 text-sm text-[#1A1A1A]">
                  <Phone size={14} weight="bold" />
                  <span className="font-mono">{connection.owner.replace("@s.whatsapp.net", "")}</span>
                </div>
              )}
              {connection.profile_name && (
                <div className="text-xs text-[#5C5C59] mt-1">{connection.profile_name}</div>
              )}
              <div className="text-[10px] font-mono text-[#5C5C59] mt-2">
                {connection.instance_name}
              </div>
            </div>
          </div>

          <div className="flex gap-2">
            {(state === "connecting" || state === "close") && (
              <Button
                onClick={refresh} disabled={actionLoading} variant="outline"
                data-testid="refresh-connection-btn"
                className="rounded-none border-[#E5E5E0]"
              >
                <ArrowClockwise size={16} weight="bold" className="mr-2" />
                Novo QR
              </Button>
            )}
            <Button
              onClick={disconnect} disabled={actionLoading} variant="outline"
              data-testid="disconnect-btn"
              className="rounded-none border-[#DC2626] text-[#DC2626] hover:bg-[#DC2626] hover:text-white"
            >
              <Trash size={16} weight="bold" className="mr-2" />
              Remover
            </Button>
          </div>
        </div>
      </div>

      {/* Per-connection settings */}
      <ConnectionSettingsPanel connection={connection} onChange={onChange} />

      <div className="mt-4">
        <ConnectionHealth connectionId={connection.id} />
      </div>

      {(state === "connecting" || state === "close") && (
        <div className="mt-6 bg-white border border-[#E5E5E0] shadow-brutal p-6 grid md:grid-cols-[280px_1fr] gap-6 items-start" data-testid="qr-card">
          <div>
            <div className="bg-white border border-[#E5E5E0] p-4 aspect-square flex items-center justify-center">
              {qrCode ? (
                <QRCodeSVG
                  value={qrCode}
                  size={240}
                  level="L"
                  data-testid="qr-svg"
                  bgColor="#FFFFFF"
                  fgColor="#0F4C3A"
                />
              ) : (
                <div className="text-center">
                  <QrCode size={48} weight="duotone" className="text-[#5C5C59] mx-auto" />
                  <div className="text-xs text-[#5C5C59] mt-3">Gerando QR code…</div>
                </div>
              )}
            </div>
            {qrCode && (
              <div className="mt-3 flex items-center justify-between text-xs">
                <span className="text-[#5C5C59]">Expira em</span>
                <span
                  className={`font-mono font-bold ${secondsLeft <= 10 ? "text-[#DC2626]" : "text-[#0F4C3A]"}`}
                  data-testid="qr-countdown"
                >
                  {secondsLeft}s
                </span>
              </div>
            )}
          </div>

          <div>
            <p className="text-[10px] uppercase tracking-[0.3em] font-semibold text-[#0F4C3A]">Pareamento</p>
            <h3 className="text-2xl font-bold tracking-tight mt-1">Como conectar</h3>
            <ol className="mt-4 space-y-3 text-sm text-[#1A1A1A]">
              <li className="flex gap-3">
                <span className="h-6 w-6 bg-[#0F4C3A] text-white flex items-center justify-center text-xs font-bold shrink-0">1</span>
                <span>Abra o <b>WhatsApp</b> no seu celular</span>
              </li>
              <li className="flex gap-3">
                <span className="h-6 w-6 bg-[#0F4C3A] text-white flex items-center justify-center text-xs font-bold shrink-0">2</span>
                <span>Toque em <b>Configurações</b> (Android: ⋮ · iOS: engrenagem)</span>
              </li>
              <li className="flex gap-3">
                <span className="h-6 w-6 bg-[#0F4C3A] text-white flex items-center justify-center text-xs font-bold shrink-0">3</span>
                <span>Selecione <b>Aparelhos conectados</b> → <b>Conectar um aparelho</b></span>
              </li>
              <li className="flex gap-3">
                <span className="h-6 w-6 bg-[#0F4C3A] text-white flex items-center justify-center text-xs font-bold shrink-0">4</span>
                <span>Aponte a câmera para este QR code (expira em ~40s)</span>
              </li>
            </ol>
          </div>
        </div>
      )}

      {state === "open" && (
        <div className="mt-6 p-6 bg-[#0F4C3A] text-white shadow-brutal">
          <div className="flex items-start gap-3">
            <WhatsappLogo size={30} weight="duotone" className="text-[#22A882]" />
            <div>
              <p className="text-[10px] uppercase tracking-[0.3em] font-semibold text-[#22A882]">Tudo pronto</p>
              <h3 className="text-2xl font-bold tracking-tight mt-1">Este número está online</h3>
              <p className="text-sm text-[#c8d5cf] mt-2 max-w-lg">
                Mensagens recebidas neste número aparecerão como novos tickets no Kanban. Respostas enviadas do Chat sairão por este número.
              </p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// -------------------- New connection dialog --------------------
const PROVIDERS = [
  { id: "evolution", name: "Evolution", badge: "Grátis", desc: "Provedor incluso na assinatura. Recomendado.", available: true },
  { id: "meta_cloud", name: "WhatsApp Cloud API (Meta)", badge: "Oficial", desc: "Oficial da Meta. Requer cadastro no Meta Business.", available: true },
  { id: "wppconnect", name: "WPPConnect", badge: "Em breve", desc: "Open source similar ao Evolution.", available: false },
  { id: "baileys", name: "Baileys direto", badge: "Em breve", desc: "Biblioteca crua, controle total.", available: false },
  { id: "twilio", name: "Twilio WhatsApp", badge: "Em breve", desc: "Pago, super estável (Twilio).", available: false },
  { id: "z_api", name: "Z-API", badge: "Em breve", desc: "Provedor brasileiro.", available: false },
  { id: "uazapi", name: "Uazapi", badge: "Em breve", desc: "Provedor brasileiro.", available: false },
  { id: "chatpro", name: "Chat-Pro", badge: "Em breve", desc: "Provedor brasileiro.", available: false },
];

function NewConnectionDialog({ open, onOpenChange, onCreated }) {
  const [label, setLabel] = useState("");
  const [provider, setProvider] = useState("evolution");
  const [metaCreds, setMetaCreds] = useState({ access_token: "", phone_number_id: "", waba_id: "", verify_token: "" });
  const [loading, setLoading] = useState(false);

  const setMc = (k, v) => setMetaCreds((c) => ({ ...c, [k]: v }));

  const submit = async () => {
    setLoading(true);
    try {
      const payload = { label: label.trim() || null, provider };
      if (provider === "meta_cloud") {
        payload.meta_credentials = {
          access_token: metaCreds.access_token.trim(),
          phone_number_id: metaCreds.phone_number_id.trim(),
          waba_id: metaCreds.waba_id.trim() || null,
          verify_token: metaCreds.verify_token.trim() || null,
        };
      }
      const { data } = await api.post("/whatsapp/connections", payload);
      toast.success(provider === "meta_cloud" ? "Conexão Meta Cloud criada!" : "Instância criada. Aguarde o QR code…");
      onOpenChange(false);
      setLabel("");
      setProvider("evolution");
      setMetaCreds({ access_token: "", phone_number_id: "", waba_id: "", verify_token: "" });
      await onCreated?.(data);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-white rounded-none border-[#E5E5E0] max-w-2xl">
        <DialogHeader>
          <DialogTitle>Nova conexão WhatsApp</DialogTitle>
        </DialogHeader>
        <div className="space-y-4">
          <div>
            <Label className="text-xs uppercase tracking-wider font-semibold">Rótulo (opcional)</Label>
            <Input
              data-testid="new-connection-label-input"
              placeholder="Ex.: Comercial, Suporte, Vendas SP"
              value={label}
              onChange={(e) => setLabel(e.target.value)}
              className="rounded-none mt-1"
            />
          </div>
          <div>
            <Label className="text-xs uppercase tracking-wider font-semibold">Provedor</Label>
            <div className="mt-2 grid sm:grid-cols-2 gap-2 max-h-[240px] overflow-y-auto pr-1">
              {PROVIDERS.map((p) => {
                const selected = provider === p.id;
                return (
                  <button
                    key={p.id}
                    type="button"
                    onClick={() => p.available && setProvider(p.id)}
                    disabled={!p.available}
                    data-testid={`provider-${p.id}`}
                    className={`text-left p-3 border-2 transition-all ${
                      selected
                        ? "border-[#0F4C3A] bg-[#EDF3F0]"
                        : p.available
                          ? "border-[#E5E5E0] hover:border-[#C7C7C0] bg-white cursor-pointer"
                          : "border-[#E5E5E0] bg-[#F7F7F5] opacity-60 cursor-not-allowed"
                    }`}
                  >
                    <div className="flex items-center justify-between">
                      <span className="font-black text-sm">{p.name}</span>
                      <span className={`text-[9px] uppercase tracking-widest font-bold px-1.5 py-0.5 ${
                        p.id === "evolution" ? "bg-[#0F4C3A] text-white" : p.id === "meta_cloud" ? "bg-[#1877F2] text-white" : "bg-[#D97706] text-white"
                      }`}>{p.badge}</span>
                    </div>
                    <p className="text-[11px] text-[#5C5C59] mt-1">{p.desc}</p>
                  </button>
                );
              })}
            </div>
          </div>

          {provider === "meta_cloud" && (
            <div className="p-4 bg-[#F0F7FF] border-l-[3px] border-[#1877F2] space-y-3" data-testid="meta-creds-section">
              <p className="text-xs font-semibold text-[#1877F2]">Credenciais Meta Cloud API</p>
              <p className="text-[11px] text-[#5C5C59]">
                Pega em <a href="https://developers.facebook.com" target="_blank" rel="noopener noreferrer" className="underline">developers.facebook.com</a> → seu App → WhatsApp → API Setup.
              </p>
              <div>
                <Label className="text-xs">Access Token</Label>
                <Input data-testid="meta-token-input" value={metaCreds.access_token} onChange={(e) => setMc("access_token", e.target.value)} className="rounded-none mt-1 font-mono text-xs" placeholder="EAAxx..." />
              </div>
              <div className="grid grid-cols-2 gap-2">
                <div>
                  <Label className="text-xs">Phone Number ID</Label>
                  <Input data-testid="meta-phone-id-input" value={metaCreds.phone_number_id} onChange={(e) => setMc("phone_number_id", e.target.value)} className="rounded-none mt-1 font-mono text-xs" placeholder="1234567890" />
                </div>
                <div>
                  <Label className="text-xs">WABA ID (opcional)</Label>
                  <Input data-testid="meta-waba-input" value={metaCreds.waba_id} onChange={(e) => setMc("waba_id", e.target.value)} className="rounded-none mt-1 font-mono text-xs" placeholder="9876543210" />
                </div>
              </div>
            </div>
          )}

          <p className="text-[11px] text-[#5C5C59]">
            Custo dos provedores oficiais/pagos é do cliente (não incluso na assinatura).
          </p>
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={() => onOpenChange(false)} className="rounded-none">Cancelar</Button>
          <Button
            data-testid="new-connection-submit-btn"
            onClick={submit}
            disabled={loading}
            className="bg-[#0F4C3A] hover:bg-[#0A3629] text-white rounded-none"
          >
            <PlugsConnected size={16} weight="bold" className="mr-2" />
            {loading ? "Criando…" : "Criar"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// -------------------- Empty state --------------------
function EmptyState({ onCreate, disabled }) {
  return (
    <div className="bg-white border border-[#E5E5E0] shadow-brutal p-10 text-center" data-testid="connections-empty">
      <WhatsappLogo size={48} weight="duotone" className="mx-auto text-[#0F4C3A]" />
      <h3 className="text-xl font-bold tracking-tight mt-3">Nenhum número conectado</h3>
      <p className="text-sm text-[#5C5C59] mt-1 max-w-md mx-auto">
        Vincule seu primeiro WhatsApp para receber mensagens no Kanban e responder pelo Chat.
      </p>
      <Button
        onClick={onCreate}
        disabled={disabled}
        data-testid="empty-new-connection-btn"
        className="mt-6 bg-[#0F4C3A] hover:bg-[#0A3629] text-white rounded-none shadow-brutal disabled:opacity-40"
      >
        <Plus size={16} weight="bold" className="mr-2" />
        Conectar primeiro número
      </Button>
    </div>
  );
}

// -------------------- Diagnostic (unchanged from previous version) --------------------
function DiagnosticPanel() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [reconfigLoading, setReconfigLoading] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/whatsapp/diagnostic");
      setData(data);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setLoading(false);
    }
  };

  const reconfig = async () => {
    setReconfigLoading(true);
    try {
      await api.post("/whatsapp/reconfigure-webhook");
      toast.success("Webhook reconfigurado em todas as instâncias");
      setTimeout(load, 1500);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setReconfigLoading(false);
    }
  };

  useEffect(() => {
    load();
    const t = setInterval(load, 8000);
    return () => clearInterval(t);
  }, []);

  if (!data) return null;
  const s = data.messages_summary || {};
  return (
    <div className="mt-6 bg-white border border-[#E5E5E0] shadow-brutal p-6" data-testid="diagnostic-panel">
      <div className="flex items-start justify-between mb-4">
        <div>
          <p className="text-[10px] uppercase tracking-[0.3em] font-semibold text-[#0F4C3A]">Diagnóstico</p>
          <h3 className="text-xl font-bold tracking-tight mt-1">O que a Evolution está enviando</h3>
          <p className="text-xs text-[#5C5C59] mt-1">Atualizado automaticamente a cada 8s</p>
        </div>
        <Button
          onClick={reconfig} disabled={reconfigLoading} variant="outline"
          data-testid="reconfigure-webhook-btn"
          className="rounded-none border-[#E5E5E0] text-xs"
        >
          <ArrowClockwise size={14} weight="bold" className="mr-1.5" />
          Reconfigurar webhooks
        </Button>
      </div>
      <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
        <StatBox label="Total webhooks" value={data.total_webhooks} tone="neutral" />
        <StatBox label="Diretas" value={s.direct} tone="good" />
        <StatBox label="Grupos" value={s.group} tone="warn" hint="ignoradas" />
        <StatBox label="Broadcast" value={s.broadcast_or_newsletter} tone="warn" hint="ignoradas" />
        <StatBox label="Enviadas por você" value={s.from_me_included} tone="neutral" />
      </div>
      {data.last_webhook_at ? (
        <div className="mt-4 text-xs text-[#5C5C59]" data-testid="last-webhook-info">
          <span className="uppercase tracking-widest font-semibold">Último webhook recebido:</span>{" "}
          <span className="font-mono text-[#0F4C3A]">{new Date(data.last_webhook_at).toLocaleString("pt-BR")}</span>
        </div>
      ) : (
        <div className="mt-4 p-3 border-l-[3px] border-[#DC2626] bg-red-50 text-xs" data-testid="no-webhook-warning">
          <b className="text-[#DC2626]">Nenhum webhook recebido.</b>
          <span className="text-[#5C5C59] ml-1">
            Se você tem mensagens no WhatsApp mas não estão aparecendo aqui, verifique se o <b>Meta AI</b> está desativado nas configurações do seu WhatsApp Business, ou clique em <b>Reconfigurar webhooks</b> acima.
          </span>
        </div>
      )}
    </div>
  );
}

function StatBox({ label, value, tone, hint }) {
  const colors = { good: "text-[#0F4C3A]", warn: "text-[#D97706]", neutral: "text-[#1A1A1A]" };
  return (
    <div className="border border-[#E5E5E0] p-3">
      <div className="text-[9px] uppercase tracking-widest font-semibold text-[#5C5C59]">{label}</div>
      <div className={`text-2xl font-black mt-1 ${colors[tone]}`}>{value ?? 0}</div>
      {hint && <div className="text-[9px] text-[#5C5C59]">{hint}</div>}
    </div>
  );
}

// -------------------- Per-connection settings toggles --------------------
function ConnectionSettingsPanel({ connection, onChange }) {
  const [values, setValues] = useState({
    chatbot_enabled: connection.chatbot_enabled ?? true,
    transcription_enabled: connection.transcription_enabled ?? false,
    block_calls: connection.block_calls ?? false,
  });
  const [saving, setSaving] = useState(null);

  useEffect(() => {
    setValues({
      chatbot_enabled: connection.chatbot_enabled ?? true,
      transcription_enabled: connection.transcription_enabled ?? false,
      block_calls: connection.block_calls ?? false,
    });
  }, [connection.id, connection.chatbot_enabled, connection.transcription_enabled, connection.block_calls]);

  const patch = async (key, val) => {
    setSaving(key);
    setValues((prev) => ({ ...prev, [key]: val }));
    try {
      await api.patch(`/whatsapp/connections/${connection.id}/settings`, { [key]: val });
      toast.success("Ajuste salvo");
      onChange?.();
    } catch (err) {
      toast.error(formatApiError(err));
      setValues((prev) => ({ ...prev, [key]: !val }));
    } finally {
      setSaving(null);
    }
  };

  const Row = ({ icon: Icon, testId, label, desc, k }) => (
    <div className="flex items-start justify-between gap-4 py-3 border-b border-[#E5E5E0] last:border-b-0" data-testid={testId}>
      <div className="flex items-start gap-3 flex-1 min-w-0">
        <div className="w-8 h-8 bg-[#EDF3F0] flex items-center justify-center flex-shrink-0">
          <Icon size={16} weight="bold" className="text-[#0F4C3A]" />
        </div>
        <div className="min-w-0">
          <div className="text-sm font-semibold">{label}</div>
          <div className="text-[11px] text-[#5C5C59] mt-0.5 leading-relaxed">{desc}</div>
        </div>
      </div>
      <Switch
        checked={values[k]}
        onCheckedChange={(v) => patch(k, v)}
        disabled={saving === k}
        data-testid={`${testId}-switch`}
      />
    </div>
  );

  return (
    <div className="bg-white border-2 border-[#E5E5E0] p-5 md:p-6" data-testid="connection-settings-panel">
      <h3 className="text-lg font-black tracking-tight mb-1">Ajustes desta conexão</h3>
      <p className="text-xs text-[#5C5C59] mb-3">Vale só para este dispositivo. Cada celular pode ter configurações diferentes.</p>
      <Row
        icon={Robot} testId="setting-chatbot" k="chatbot_enabled"
        label="Chatbot"
        desc="Deixa o chatbot automático responder as mensagens recebidas neste número."
      />
      <Row
        icon={SpeakerHigh} testId="setting-transcription" k="transcription_enabled"
        label="Transcrição de áudio"
        desc="Converte áudios recebidos em texto pro atendente ler sem escutar. (em breve)"
      />
      <Row
        icon={PhoneSlash} testId="setting-block-calls" k="block_calls"
        label="Bloquear ligações"
        desc="Recusa automaticamente chamadas de voz e vídeo neste número."
      />
    </div>
  );
}
